import { useState, useCallback, useMemo } from 'react';
import { toast } from '@lb-classroom/ui';
import {
  useCreateEducationMutation,
  useUpdateEducationMutation,
} from '@root/hooks/queries';
import EducationItemValidator from '@root/lib/validators/EducationItemValidator';
import type {
  EditEducationHistoryModalContainerProps,
  ValidationResult,
} from './edit-education-history-modal.types';

const DEFAULT_VALIDATION: ValidationResult = { success: true, message: '' };

export const useEditEducationHistoryModalService = (
  props: EditEducationHistoryModalContainerProps,
) => {
  const { hideModal, isNew = false, education } = props;

  const [hasChanges, setHasChanges] = useState(false);

  // Mutations
  const createEducationMutation = useCreateEducationMutation();
  const updateEducationMutation = useUpdateEducationMutation();
  const isSubmitting = createEducationMutation.isPending || updateEducationMutation.isPending;

  // Form fields
  const [degree, setDegree] = useState(education.degree || '');
  const [major, setMajor] = useState(education.major || '');
  const [universityName, setUniversityName] = useState(
    education.university_name || '',
  );
  const [courseName, setCourseName] = useState(education.course_name || '');
  const [yearFrom, setYearFrom] = useState<number | null>(education.year_from);
  const [yearTo, setYearTo] = useState<number | null>(education.year_to);

  // Validation states
  const [degreeValidation, setDegreeValidation] =
    useState<ValidationResult>(DEFAULT_VALIDATION);
  const [universityValidation, setUniversityValidation] =
    useState<ValidationResult>(DEFAULT_VALIDATION);
  const [courseValidation, setCourseValidation] =
    useState<ValidationResult>(DEFAULT_VALIDATION);
  const [yearRangeValidation, setYearRangeValidation] =
    useState<ValidationResult>(DEFAULT_VALIDATION);

  // Year options
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years: Array<{ name: string; value: number | null }> = [];
    years.push({ name: 'Select Year', value: null });
    for (let year = 1960; year <= currentYear + 4; year++) {
      years.push({ name: year.toString(), value: year });
    }
    return years;
  }, []);

  const handleFieldChange = useCallback(
    <T>(setter: React.Dispatch<React.SetStateAction<T>>) =>
      (value: T) => {
        setter(value);
        setHasChanges(true);
      },
    [],
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      const data = {
        degree,
        university_name: universityName,
        course_name: courseName,
        year_from: yearFrom,
        year_to: yearTo,
      };

      const validator = new EducationItemValidator(data);
      const result = (await validator.validate()) as {
        valid: boolean;
        errors?: Array<{ type: string; message: string }>;
      };

      if (!result.valid) {
        const errors = result.errors || [];

        const degreeError = errors.find(
          (e: { type: string }) => e.type === 'Degree',
        );
        const universityError = errors.find(
          (e: { type: string }) => e.type === 'University Name',
        );
        const courseError = errors.find(
          (e: { type: string }) => e.type === 'Course',
        );
        const yearRangeError = errors.find(
          (e: { type: string }) => e.type === 'Duration of Degree',
        );

        setDegreeValidation(
          degreeError
            ? { success: false, message: degreeError.message }
            : DEFAULT_VALIDATION,
        );
        setUniversityValidation(
          universityError
            ? { success: false, message: universityError.message }
            : DEFAULT_VALIDATION,
        );
        setCourseValidation(
          courseError
            ? { success: false, message: courseError.message }
            : DEFAULT_VALIDATION,
        );
        setYearRangeValidation(
          yearRangeError
            ? { success: false, message: yearRangeError.message }
            : DEFAULT_VALIDATION,
        );
        return;
      }

      // Clear validation errors
      setDegreeValidation(DEFAULT_VALIDATION);
      setUniversityValidation(DEFAULT_VALIDATION);
      setCourseValidation(DEFAULT_VALIDATION);
      setYearRangeValidation(DEFAULT_VALIDATION);

      const params = {
        ...data,
        major,
      };

      const mutationOptions = {
        onSuccess: () => {
          toast.success(
            isNew
              ? 'Successfully created the education history.'
              : 'Successfully updated the education history.'
          );
          hideModal();
        },
        onError: (error: Error) => {
          const errorMessage = error.message || 'Failed to save education history';
          toast.error(errorMessage);
        },
      };

      if (isNew) {
        createEducationMutation.mutate(params, mutationOptions);
      } else {
        updateEducationMutation.mutate({ id: String(education.id), ...params }, mutationOptions);
      }
    },
    [
      degree,
      universityName,
      courseName,
      yearFrom,
      yearTo,
      major,
      education.id,
      isNew,
      hideModal,
      createEducationMutation,
      updateEducationMutation,
    ],
  );

  return {
    isNew,
    isSubmitting,
    hasChanges,
    degree,
    major,
    universityName,
    courseName,
    yearFrom,
    yearTo,
    yearOptions,
    degreeValidation,
    universityValidation,
    courseValidation,
    yearRangeValidation,
    message: props.message,
    messageType: props.messageType,
    onDegreeChange: handleFieldChange(setDegree),
    onMajorChange: handleFieldChange(setMajor),
    onUniversityNameChange: handleFieldChange(setUniversityName),
    onCourseNameChange: handleFieldChange(setCourseName),
    onYearFromChange: handleFieldChange(setYearFrom),
    onYearToChange: handleFieldChange(setYearTo),
    onSubmit: handleSubmit,
    onClose: hideModal,
  };
};
